import React, { useLayoutEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'

import { EllipsisDropdownAction } from '@sketch/components'
import { useToast } from '@sketch/toasts'
import {
  NotificationInfoFragment,
  useAcceptShareMembershipInvitationMutation,
  useNotificationMarkAsReadMutation,
  useSetNotificationSubscriptionMutation,
  useUpdateNotificationMutation,
} from '@sketch/gql-types'

import { ReactComponent as ChatBubbleIcon } from '@sketch/icons/chat-bubble-16'
import { ReactComponent as CheckmarkCircleIcon } from '@sketch/icons/checkmark-circle-17'
import { ReactComponent as DownloadIcon } from '@sketch/icons/arrow-down-to-line-16'
import { ReactComponent as PinIcon } from '@sketch/icons/pin-16'
import { ReactComponent as ShareIcon } from '@sketch/icons/arrow-top-right-diagonal-16'

import {
  ActionButton,
  ActionLabel,
  ActionsBar,
  ActionsDivider,
  StickyActions,
} from './InboxPreview.styles'
import { getNotificationPath } from './getNotificationPath'
import { recordPinned, recordUnpinned } from '../../views/Inbox/pinOrder'

/** Collapse to icon-only this many pixels before the labels actually overflow. */
const COLLAPSE_THRESHOLD = 30

const SeeDocumentAction = ({ path }: { path: string }) => {
  const history = useHistory()
  return (
    <ActionButton
      variant="tertiary"
      size="32"
      icon={ShareIcon}
      iconPosition="left"
      onClick={() => history.push(path)}
    >
      <ActionLabel>See Document</ActionLabel>
    </ActionButton>
  )
}

interface DownloadActionProps {
  identifier: string
  assetPath: string
}

const DownloadAction = ({ identifier, assetPath }: DownloadActionProps) => {
  const [markAsRead] = useNotificationMarkAsReadMutation({
    variables: { identifier },
    onError: 'show-toast',
    redirectErrors: true,
  })
  return (
    <ActionButton
      variant="primary"
      size="32"
      icon={DownloadIcon}
      iconPosition="left"
      onClick={() => {
        window.open(assetPath, '_self')
        markAsRead()
      }}
    >
      <ActionLabel>Download</ActionLabel>
    </ActionButton>
  )
}

const UnfollowThreadAction = ({
  annotationIdentifier,
}: {
  annotationIdentifier: string | undefined
}) => {
  const { showToast } = useToast()
  const [setSubscription] = useSetNotificationSubscriptionMutation({
    onError: 'show-toast',
    onCompleted: data =>
      showToast(
        data.setNotificationSubscription.share?.subscriptionStatus === 'ON'
          ? 'Following'
          : 'Unfollowed'
      ),
  })
  return (
    <ActionButton
      variant="tertiary"
      size="32"
      icon={ChatBubbleIcon}
      iconPosition="left"
      onClick={() =>
        setSubscription({
          variables: { state: 'OFF', annotationIdentifier },
        })
      }
    >
      <ActionLabel>Unfollow</ActionLabel>
    </ActionButton>
  )
}

const AcceptAction = ({ shareIdentifier }: { shareIdentifier: string }) => {
  const { showToast } = useToast()
  const [acceptInvitation] = useAcceptShareMembershipInvitationMutation({
    onError: 'show-toast',
    onCompleted: () => showToast('You now have access to this document'),
  })
  return (
    <ActionButton
      variant="primary"
      size="32"
      icon={CheckmarkCircleIcon}
      iconPosition="left"
      onClick={() => acceptInvitation({ variables: { shareIdentifier } })}
    >
      <ActionLabel>Accept</ActionLabel>
    </ActionButton>
  )
}

const PinAction = ({
  notification,
}: {
  notification: NotificationInfoFragment
}) => {
  const { showToast } = useToast()
  const { identifier, isPinned } = notification

  const [updateNotification, { loading }] = useUpdateNotificationMutation({
    onError: 'show-toast',
    redirectErrors: true,
    // Base the result on the state the server returned, not the captured
    // `isPinned` — by the time this fires the cache has already updated and
    // this closure would read the post-toggle value. Record the pin order so
    // the inbox list can surface the most recently pinned first.
    onCompleted: data => {
      const nowPinned = data.updateNotification?.notification?.isPinned
      if (nowPinned) {
        recordPinned(identifier)
      } else {
        recordUnpinned(identifier)
      }
      showToast(nowPinned ? 'Pinned' : 'Unpinned')
    },
  })

  return (
    <ActionButton
      variant="tertiary"
      size="32"
      icon={PinIcon}
      iconPosition="left"
      // Show a spinner and block re-clicks while the toggle is in flight —
      // the bar only reorders/labels once the server confirms the new state.
      loading={loading}
      onClick={() =>
        updateNotification({
          variables: { identifier, input: { pin: !isPinned } },
        })
      }
    >
      <ActionLabel>{isPinned ? 'Unpin' : 'Pin'}</ActionLabel>
    </ActionButton>
  )
}

interface ResolvedAction {
  id: string
  element: React.ReactElement
}

/**
 * Builds the list of action elements applicable to a notification. The
 * returned elements are not mounted yet — React only runs each component's
 * hooks when the element is actually rendered, so unused actions never pay the
 * cost of their mutation hook.
 */
const resolveActions = (
  notification: NotificationInfoFragment
): ResolvedAction[] => {
  const path = getNotificationPath(notification)

  // Pin/unpin applies to every notification type.
  const actions: ResolvedAction[] = [
    { id: 'pin', element: <PinAction notification={notification} /> },
  ]

  const pushSeeDocument = () => {
    if (path) {
      actions.push({
        id: 'see-document',
        element: <SeeDocumentAction path={path} />,
      })
    }
  }

  switch (notification.__typename) {
    case 'CommentNotification':
    case 'MentionNotification': {
      const isSubscribed = notification.share?.subscriptionStatus === 'ON'

      if (isSubscribed) {
        actions.push({
          id: 'unfollow',
          element: (
            <UnfollowThreadAction
              annotationIdentifier={notification.comment?.annotation.identifier}
            />
          ),
        })
      }
      pushSeeDocument()
      break
    }
    case 'AddShareMemberNotification': {
      pushSeeDocument()
      const hasPendingInvite =
        notification.share?.userHasUnacceptedInvitationToView === true
      if (hasPendingInvite && notification.share) {
        actions.push({
          id: 'accept',
          element: (
            <AcceptAction shareIdentifier={notification.share.identifier} />
          ),
        })
      }
      break
    }
    case 'DownloadableAssetRenderedNotification': {
      pushSeeDocument()
      const asset =
        notification.notifiedVersion?.document?.downloadableAssets[0]
      if (asset?.status === 'AVAILABLE' && asset.path) {
        actions.push({
          id: 'download',
          element: (
            <DownloadAction
              identifier={notification.identifier}
              assetPath={asset.path}
            />
          ),
        })
      }
      break
    }
    default:
      pushSeeDocument()
  }

  return actions
}

interface InboxPreviewActionsProps {
  notification: NotificationInfoFragment
  scrolled?: boolean
}

export const InboxPreviewActions = ({
  notification,
  scrolled,
}: InboxPreviewActionsProps) => {
  const actions = resolveActions(notification)
  const barRef = useRef<HTMLDivElement>(null)

  // Re-measure only when the rendered action set changes, not on every render
  // (the actions array is a new reference each render).
  const actionsKey = actions.map(action => action.id).join('|')

  // Collapse the bar to icons-only when the labelled buttons don't fit on a
  // single row. We measure the bar's content (`scrollWidth`) against the
  // available width of its parent — which is reliably constrained, whereas the
  // bar is capped at that width and clips its own content. The data attribute
  // is toggled imperatively (not via state) so the measurement always runs
  // against the labelled width, avoiding a measure → re-render → re-measure
  // loop.
  useLayoutEffect(() => {
    const bar = barRef.current
    const container = bar?.parentElement
    if (!bar || !container || typeof ResizeObserver === 'undefined') return

    const evaluate = () => {
      bar.removeAttribute('data-icon-only')
      if (bar.scrollWidth > container.clientWidth - COLLAPSE_THRESHOLD) {
        bar.setAttribute('data-icon-only', 'true')
      }
    }

    evaluate()
    const observer = new ResizeObserver(evaluate)
    observer.observe(container)
    return () => observer.disconnect()
  }, [actionsKey])

  return (
    <StickyActions $scrolled={scrolled}>
      <ActionsBar ref={barRef}>
        {actions.map(action => (
          <React.Fragment key={action.id}>{action.element}</React.Fragment>
        ))}
        {actions.length > 0 && <ActionsDivider aria-hidden />}
        <EllipsisDropdownAction label="More actions" />
      </ActionsBar>
    </StickyActions>
  )
}
